/* PlanFocusWizard — sticky rooster-header met per-groep BKR-status en
   suggesties. Extracted uit RoosterPage.jsx (feb 2026 split, iter 3).

   Props (compleet zelfstandig; geen parent-closure):
     locations, groups, focusLocationId, focusGroupId
     onLocationChange, onGroupChange, datum, refreshKey, onAddShift
*/
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { FocusGauge } from "./FocusGauge";
import { SnelplannerSuggesties } from "./SnelplannerSuggesties";
import { BKRRevealPopover } from "./BKRRevealPopover";


/**
 * WEBSTER KOV — PlanFocusWizard
 *
 * Lineaire planner-flow bovenaan de dag-view:
 *   Stap 1: Kies locatie
 *   Stap 2: Kies groep (gefilterd op locatie)
 *   Stap 3: Live BKR-gauge voor die groep + "Shift toevoegen"-knop
 *
 * Live updates: haalt `/planning/day` op met `location_id` filter, zoekt de
 * gefocuste groep uit de `bkr`-array en toont een grote gauge die per shift-
 * mutatie herberekent (refreshKey trigger). Kleurt rood → oranje → groen naar-
 * mate PMs worden toegevoegd zodat de directeur ziet wanneer de dag compliant is.
 */
export function PlanFocusWizard({ locations, groups, focusLocationId, focusGroupId,
                            onLocationChange, onGroupChange, datum, refreshKey, onAddShift }) {
  const [bkrArr, setBkrArr] = useState([]);
  const activeLocations = useMemo(
    () => [...(locations || [])]
      .filter(l => l && l.active !== false)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "nl", { sensitivity: "base" })),
    [locations],
  );
  useEffect(() => {
    let cancel = false;
    if (!focusLocationId) { setBkrArr([]); return; }
    const params = { datum, location_id: focusLocationId };
    api.get("/planning/day", { params })
      .then(r => { if (!cancel) setBkrArr(r.data?.bkr || []); })
      .catch(() => { if (!cancel) setBkrArr([]); });
    return () => { cancel = true; };
    // `api` is stabiele module-level import.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datum, focusLocationId, refreshKey]);

  const availableGroups = groups.filter(g => !focusLocationId || g.location_id === focusLocationId);
  const focusBkr = focusGroupId ? bkrArr.find(b => b.group_id === focusGroupId) : null;

  return (
    <div className="mb-4 rounded-lg border-2 border-neutral-200 bg-white overflow-hidden shadow-sm"
         data-testid="plan-focus-wizard">
      {/* Stappen-header */}
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-neutral-200">
        {/* Stap 1 — locatie */}
        <div className={`p-3 ${focusLocationId ? "bg-emerald-50/40" : "bg-white"}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${focusLocationId ? "bg-emerald-600 text-white" : "bg-neutral-800 text-white"}`}>1</div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-700">Kies locatie</div>
          </div>
          <select
            value={focusLocationId || ""}
            onChange={(e) => onLocationChange(e.target.value)}
            data-testid="wizard-locatie-select"
            className="w-full text-sm border-2 rounded px-2 py-2 bg-white font-medium"
          >
            <option value="">— selecteer locatie —</option>
            {activeLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>

        {/* Stap 2 — groep */}
        <div className={`p-3 ${!focusLocationId ? "bg-neutral-50 opacity-60" : focusGroupId ? "bg-emerald-50/40" : "bg-white"}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${focusGroupId ? "bg-emerald-600 text-white" : focusLocationId ? "bg-neutral-800 text-white" : "bg-neutral-300 text-white"}`}>2</div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-700">Kies groep</div>
          </div>
          <select
            value={focusGroupId || ""}
            onChange={(e) => onGroupChange(e.target.value)}
            disabled={!focusLocationId}
            data-testid="wizard-groep-select"
            className="w-full text-sm border-2 rounded px-2 py-2 bg-white font-medium disabled:cursor-not-allowed disabled:bg-neutral-50"
          >
            <option value="">— selecteer groep —</option>
            {availableGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          {focusLocationId && availableGroups.length === 0 && (
            <div className="text-[10px] text-amber-700 mt-1">Geen groepen op deze locatie</div>
          )}
        </div>

        {/* Stap 3 — BKR-status */}
        <div className={`p-3 ${!focusGroupId ? "bg-neutral-50 opacity-60"
                              : focusBkr?.status === "ok" ? "bg-emerald-50"
                              : focusBkr?.status === "warning" ? "bg-amber-50"
                              : focusBkr?.status === "alert" ? "bg-red-50"
                              : "bg-white"}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${focusBkr?.status === "ok" ? "bg-emerald-600 text-white" : focusGroupId ? "bg-neutral-800 text-white" : "bg-neutral-300 text-white"}`}>3</div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-700">BKR live</div>
          </div>
          {!focusGroupId && <div className="text-xs text-neutral-500">Kies eerst een groep om de BKR te zien.</div>}
          {focusGroupId && !focusBkr && (
            <div className="text-xs text-neutral-500">Geen kinderen gepland op deze dag.</div>
          )}
          {focusBkr && (
            <div className="flex items-center gap-3">
              <FocusGauge bkr={focusBkr} />
              <div className="flex-1 min-w-0">
                <div className="text-lg font-bold tabular-nums leading-none"
                     style={{ color: focusBkr.status === "ok" ? "#059669" : focusBkr.status === "warning" ? "#d97706" : "#dc2626" }}>
                  {focusBkr.medewerkers_ingeroosterd}/{focusBkr.vereist} PM
                </div>
                <div className="text-[11px] text-neutral-700 mt-0.5">
                  {focusBkr.kinderen} kind{focusBkr.kinderen === 1 ? "" : "eren"}
                  {focusBkr.kinderen_afwezig > 0 && <span className="text-neutral-500"> · {focusBkr.kinderen_afwezig} afwezig</span>}
                  {focusBkr.kinderen_extra > 0 && <span className="text-blue-700"> · +{focusBkr.kinderen_extra} extra</span>}
                </div>
                <div className="text-[10px] font-medium mt-0.5 flex items-center gap-2"
                     style={{ color: focusBkr.status === "ok" ? "#059669" : focusBkr.status === "warning" ? "#d97706" : "#dc2626" }}>
                  <span>
                    {focusBkr.status === "ok" ? "✓ BKR compliant"
                      : focusBkr.status === "warning" ? "⚠ krap bezet"
                      : `✗ ${focusBkr.vereist - focusBkr.medewerkers_ingeroosterd} PM tekort`}
                  </span>
                  <BKRRevealPopover bkr={focusBkr} testid={`wizard-bkr-reveal-${focusGroupId}`} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actiebalk onder de wizard */}
      {focusGroupId && (
        <div className="border-t border-neutral-200 px-3 py-2 bg-neutral-50 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] text-neutral-600">
            Voeg medewerkers toe onder — de teller ververst direct na elke shift.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onLocationChange(""); onGroupChange(""); }}
              className="text-[11px] text-neutral-500 underline hover:text-neutral-900"
              data-testid="wizard-reset"
            >
              wissen
            </button>
            <button
              onClick={onAddShift}
              data-testid="wizard-add-shift"
              className="text-xs font-medium px-3 py-1.5 rounded bg-[color:var(--sage,#0d5e4a)] text-white hover:opacity-90"
            >
              + Shift toevoegen
            </button>
          </div>
        </div>
      )}

      {/* WEBSTER KOV — Snelplanner Tekorten: toon suggesties zodra groep in het rood/oranje staat */}
      {focusGroupId && focusBkr && focusBkr.status !== "ok" && (
        <SnelplannerSuggesties datum={datum} groupId={focusGroupId} onAdd={(mid) => {
          // Prefill add-shift dialoog met medewerker_id
          onAddShift(mid);
        }} refreshKey={refreshKey} />
      )}
    </div>
  );
}
